import { faEnvelope } from "@fortawesome/pro-solid-svg-icons/faEnvelope";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogContent, FormHelperText, Grid, Tab, Typography } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { AppTransComponents } from "app/appTrans";
import { MprcAppBar, MprcBarTabs, MprcConfirmDialog, MprcContainer, MprcDialogTitleClose, MprcLink, MprcMessageBox, MprcRequestIndicator, MprcSpinnerIndicator, MprcText } from "components";
import { NotificationSendMailOption, NotificationTab, UpdateNotificationConfigPayload, UserNotificationConfigDto } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import * as React from 'react';
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { UserChangeEmailDialog } from "views/foundation/user/dialogs/UserChangeEmailDialog";

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    onClose: () => void;
}

type Props = IProps;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////


export const NotificationConfigDialog: React.FC<Props> = ({ onClose }) => {
    const [operation, setOperation] = useState<"none" | "loading" | "updating">("loading");
    const [selectedTab, setSelectedTab] = useState<NotificationTab>(null!);
    const [updateGroup, setUpdateGroup] = useState<UpdateNotificationConfigPayload | null>(null);
    const [lastError, setLastError] = useState<any | null>(null);
    const [data, setData] = useState<UserNotificationConfigDto>(null!);
    const [dialog, setDialog] = useState<any>();

    const api = useCoreApi();
    const session = useSession();
    const snackbar = useSnackbar();
    const { t } = useTranslation();

    useEffect(() => {

        async function update() {
            api.v1.user.updateNotificationConfig(updateGroup!)
                .then(() => {
                    const newData = Object.assign({}, data);

                    var groups = newData.tabs.find(p => p.tabId == selectedTab)!.groups;
                    var item = groups.find(p => p.groupId == updateGroup!.groupId);
                    if (item) {
                        item.showOnWorkspace = updateGroup!.showWorkspaceNotification;
                        item.mailOption = updateGroup!.mailOption;
                        item.sendPushNotification = updateGroup!.sendPushNotification;
                    }
                    setData(newData);
                    setUpdateGroup(null);
                })
                .catch((error) => {
                    snackbar.enqueueSnackbar(t("User.NotificationConfigDialog.OperationFailed"), { variant: "error" });
                });
        }

        if (updateGroup != null) update();
    }, [updateGroup]);

    const handleTabChange = (event: any, value: any) => {
        setSelectedTab(value);
    };

    // Ao fechar, verifica se usuário tem e-mail e solicita se deseja preencher.
    const handleClose = () => {
        if (session.user.email == null || session.user.email.length == 0) {
            setDialog(
                <MprcConfirmDialog
                    open
                    severity="danger"
                    message={<span>{t("shared:App.Notification.ConfirmChangeEmailMessage")}</span>}
                    onClose={
                        (result) => {
                            if (result) {
                                setDialog(<UserChangeEmailDialog onClose={onClose} />);
                            }
                            else {
                                onClose();
                            }
                        }
                    }
                />
            );
        }
        else {
            onClose();
        }
    };

    const renderTabs = () => {
        return (
            <MprcAppBar position="static" color="secondary"   >
                <MprcBarTabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {
                        data.tabs.map((v) =>
                            <Tab
                                label={v.name}
                                key={v.tabId}
                                value={v.tabId}
                            />
                        )
                    }
                </MprcBarTabs>
            </MprcAppBar>
        );
    };

    const renderSelectedTab = () => {

        var groups = data.tabs.find(p => p.tabId == selectedTab)!.groups;

        return groups.map((v, index) => {

            // var mobileColor: MprcColors = v.sendPushNotification
            //     ? "highlight"
            //     : "muted";

            // var mobileMessage = t("User.NotificationConfigDialog.MobileNotification");

            var emailColor: MprcColors = v.mailOption == NotificationSendMailOption.None
                ? "muted"
                : "highlight";

            var emailMessage = t("User.NotificationConfigDialog.EmailNotification");

            return (
                <MprcContainer key={index} margin="normal">

                    <Typography variant="h3" textTransform="unset">
                        {v.name}
                    </Typography>

                    {
                        v.description && v.description.length > 0 &&
                        <FormHelperText> {v.description}</FormHelperText>
                    }

                    <Grid container columnSpacing={3} mt={0.5}>

                        <Grid>
                            {
                                v.mailOption != null &&
                                <MprcLink
                                    onClick={
                                        () => setUpdateGroup(
                                            {
                                                groupId: v.groupId,
                                                showWorkspaceNotification: v.showOnWorkspace,
                                                sendPushNotification: v.sendPushNotification,
                                                mailOption: v.mailOption == NotificationSendMailOption.None ? NotificationSendMailOption.Immediate : NotificationSendMailOption.None
                                            })
                                    }
                                >
                                    <MprcText color={emailColor}>
                                        <FontAwesomeIcon icon={faEnvelope} fixedWidth size="xl" />
                                        &nbsp;
                                        {emailMessage}
                                    </MprcText>
                                </MprcLink>
                            }
                        </Grid>

                        {/*
                        <Grid>
                            {
                                v.sendPushNotification != null &&
                                <MprcLink
                                    onClick={
                                        () => setUpdateGroup(
                                            {
                                                groupId: v.groupId,
                                                showWorkspaceNotification: v.showOnWorkspace,
                                                sendPushNotification: !v.sendPushNotification,
                                                mailOption: v.mailOption
                                            })
                                    }
                                >
                                    <MprcText color={mobileColor}>
                                        <FontAwesomeIcon icon={faMobileSignalOut} fixedWidth size="xl" />
                                        {mobileMessage}
                                    </MprcText>
                                </MprcLink>
                            }
                        </Grid>
                        */}

                    </Grid>

                </MprcContainer>
            );
        });
    };

    useEffect(() => {
        async function load() {
            try {
                var result = await api.v1.user.getNotificationsConfig();
                setData(result.data);
                setOperation("none");
                setSelectedTab(result.data.tabs[0].tabId);
            }
            catch (error) {
                setOperation("none");
                setLastError(error);
            }
        }
        load();
    }, []);

    return (
        <>
            {dialog}

            <Dialog
                open
                fullWidth
                maxWidth="sm"
            >
                <MprcDialogTitleClose
                    title={t("User.NotificationConfigDialog.Title")}
                    onClose={handleClose}
                    shrinkPadding
                />

                <DialogContent>
                    {
                        lastError != null
                            ? <MprcMessageBox variant="error" message={lastError} />
                            : operation == "loading" && data == null
                                ? <MprcSpinnerIndicator />
                                : <>
                                    {operation == "updating" && <MprcRequestIndicator type="post" />}

                                    <MprcContainer margin="dense" >
                                        {renderTabs()}
                                    </MprcContainer>

                                    <MprcContainer>
                                        <FormHelperText>
                                            <div>{t("User.NotificationConfigDialog.MessageHint_L1")}</div>
                                            <div>
                                                {
                                                    <Trans i18nKey="User.NotificationConfigDialog.MessageHint_L2"
                                                        components={AppTransComponents}
                                                    />
                                                }
                                            </div>
                                        </FormHelperText>
                                    </MprcContainer>

                                    <div style={{ height: "calc(100vh - 300px)", overflow: "auto" }} className={AppGlobalClassKeys.thinScrollbar}>
                                        {renderSelectedTab()}
                                    </div>
                                </>
                    }
                </DialogContent>
            </Dialog>
        </>
    );
};
